"use client"

import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { FirebaseLoginError, loginWithFirebaseGoogle } from "../lib/firebase-client.ts"
import { isNativeApp, nativePlatform } from "../lib/native-capabilities.ts"
import { safeNextRoute } from "../lib/safe-route.ts"
import { Icon } from "./icons.tsx"
import { ConnectionSymbol } from "./connection-symbol.tsx"

const errorMessage = (error: FirebaseLoginError): string => {
  switch (error.code) {
    case "cancelled":
      return "로그인이 취소되었습니다. 준비되면 다시 시도해 주세요."
    case "update-required":
      return "앱을 최신 버전으로 업데이트한 뒤 다시 로그인해 주세요."
    case "unavailable":
      return "로그인 설정을 확인하고 있습니다. 잠시 후 다시 시도해 주세요."
    case "failed":
      return "로그인을 완료하지 못했습니다. 연결을 확인한 뒤 다시 시도해 주세요."
  }
}

export const FirebaseLogin = ({ next }: { readonly next: string | null }) => {
  const router = useRouter()
  const [message, setMessage] = useState("")
  const [pending, setPending] = useState(false)
  const [appleAvailable, setAppleAvailable] = useState(false)
  useEffect(() => {
    const update = (): void => setAppleAvailable(isNativeApp() && nativePlatform() === "IOS")
    update()
    window.addEventListener("cashhub:native-ready", update)
    return () => window.removeEventListener("cashhub:native-ready", update)
  }, [])

  const login = async (provider: "google" | "apple" = "google"): Promise<void> => {
    if (pending) return
    setPending(true)
    setMessage("")
    try {
      const destination = await loginWithFirebaseGoogle(provider)
      router.replace(safeNextRoute(next ?? "") ?? destination)
      router.refresh()
    } catch (error: unknown) {
      if (error instanceof FirebaseLoginError) setMessage(errorMessage(error))
      else throw error
    } finally {
      setPending(false)
    }
  }

  return (
    <article className="journey-card auth-card">
      <ConnectionSymbol />
      <header className="journey-heading">
        <h1>CashHub 시작하기</h1>
        <p className="phrase-safe-block">간편하게 로그인하고 전환한 캐시와 이용 내역을 확인하세요.</p>
      </header>
      <button aria-busy={pending} className="button primary auth-button" disabled={pending} onClick={() => void login()} type="button">
        <Icon name="account" />
        {pending ? "로그인 확인 중" : "Google로 계속하기"}
      </button>
      {appleAvailable ? (
        <button className="button secondary auth-button" disabled={pending} onClick={() => void login("apple")} type="button">
          Apple로 계속하기
        </button>
      ) : null}
      <p className="auth-privacy">로그인 정보는 안전하게 보호됩니다.</p>
      {message.length === 0 ? null : (
        <p aria-live="assertive" className="error-copy auth-message">
          {message}
        </p>
      )}
    </article>
  )
}
